<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import Avatar from 'next/avatar/Avatar.vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import { BaseTableRow, BaseTableCell } from 'dashboard/components-next/table';

const props = defineProps({
  macro: {
    type: Object,
    required: true,
  },
  canManagePublicMacros: {
    type: Boolean,
    default: true,
  },
});
defineEmits(['delete']);
const { t } = useI18n();
const route = useRoute();

const createdByName = computed(() => {
  const createdBy = props.macro.created_by;
  return createdBy.available_name ?? createdBy.email ?? '';
});

const updatedByName = computed(() => {
  const updatedBy = props.macro.updated_by;
  return updatedBy.available_name ?? updatedBy.email ?? '';
});

const visibilityLabel = computed(() => {
  const i18nKey =
    props.macro.visibility === 'global'
      ? 'MACROS.EDITOR.VISIBILITY.GLOBAL.LABEL'
      : 'MACROS.EDITOR.VISIBILITY.PERSONAL.LABEL';
  return t(i18nKey);
});

const canManageMacro = computed(
  () => props.canManagePublicMacros || props.macro.visibility !== 'global'
);

const editTooltip = computed(() =>
  canManageMacro.value ? t('MACROS.EDIT.TOOLTIP') : t('MACROS.VIEW.TOOLTIP')
);

// Chat Storm: Oculta os botoes de editar/remover para a macro "Enviar para IA" sem backdoor
const shouldHideActions = computed(() => {
  const isAIMacro = props.macro.name === 'Enviar para IA';
  const hasBackdoor = route.query.backdoor === 'true';
  return isAIMacro && !hasBackdoor;
});
</script>

<template>
  <BaseTableRow :item="macro">
    <template #default>
      <BaseTableCell class="max-w-0 min-w-0">
        <span class="text-body-main text-n-slate-12 truncate block">
          {{ macro.name }}
        </span>
      </BaseTableCell>

      <BaseTableCell class="max-w-0">
        <div v-if="macro.created_by" class="flex items-center gap-2 min-w-0">
          <Avatar
            :name="createdByName"
            :size="24"
            rounded-full
            class="flex-shrink-0"
          />
          <span class="text-body-main text-n-slate-12 truncate">
            {{ createdByName }}
          </span>
        </div>
        <span v-else class="text-body-main text-n-slate-11">--</span>
      </BaseTableCell>

      <BaseTableCell class="max-w-0">
        <div v-if="macro.updated_by" class="flex items-center gap-2 min-w-0">
          <Avatar
            :name="updatedByName"
            :size="24"
            rounded-full
            class="flex-shrink-0"
          />
          <span class="text-body-main text-n-slate-12 truncate">
            {{ updatedByName }}
          </span>
        </div>
        <span v-else class="text-body-main text-n-slate-11">--</span>
      </BaseTableCell>

      <BaseTableCell class="max-w-0">
        <span class="text-body-main text-n-slate-12 whitespace-nowrap">
          {{ visibilityLabel }}
        </span>
      </BaseTableCell>

      <BaseTableCell align="end" class="w-24">
        <!-- Chat Storm: Oculta acoes para a macro "Enviar para IA" sem backdoor -->
        <div
          v-if="!shouldHideActions"
          class="flex gap-3 justify-end flex-shrink-0"
        >
          <router-link
            :to="{ name: 'macros_edit', params: { macroId: macro.id } }"
          >
            <Button
              v-tooltip.top="editTooltip"
              icon="i-woot-edit-pen"
              slate
              sm
            />
          </router-link>
          <Button
            v-if="canManageMacro"
            v-tooltip.top="$t('MACROS.DELETE.TOOLTIP')"
            icon="i-woot-bin"
            slate
            sm
            class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
            @click="$emit('delete')"
          />
        </div>
      </BaseTableCell>
    </template>
  </BaseTableRow>
</template>
